import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {
  Overlay,
  CloseButton,
  MainArea,
  VideoFrame,
  NavButton,
  ThumbStrip,
  ThumbItem,
  VideoThumb,
} from './ProductMediaLightbox.style';
import ImageComponent from '../../components/Image/Image';
import {
  IMAGE_SIZE,
  IMAGE_UNAVAILABLE_SIZE,
  KEYBOARD_KEYS,
} from '../../constants/constants';
import {reduceImageSize} from '../../helpers/image.helper';
import {sizes} from '../../styles/components/media-queries';

const getFullscreenImageUrl = url => {
  if (!url) {
    return url;
  }
  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth <= sizes.small;
  return reduceImageSize(
    url,
    false,
    isMobileViewport ? IMAGE_SIZE.FULLSCREEN_MOBILE : IMAGE_SIZE.FULLSCREEN
  );
};

const ProductMediaLightbox = ({media, initialIndex, name, onClose}) => {
  const [index, setIndex] = useState(initialIndex || 0);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === KEYBOARD_KEYS.ESCAPE) {
        onClose();
      } else if (e.key === KEYBOARD_KEYS.ARROW_LEFT) {
        setIndex(current => (current > 0 ? current - 1 : current));
      } else if (e.key === KEYBOARD_KEYS.ARROW_RIGHT) {
        setIndex(current =>
          current < media.length - 1 ? current + 1 : current
        );
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [media.length, onClose]);

  const currentMedia = media[index];
  const hasPrev = index > 0;
  const hasNext = index < media.length - 1;

  const stopPropagation = e => e.stopPropagation();

  return (
    <Overlay onClick={onClose}>
      <CloseButton onClick={onClose} aria-label="Close">
        &#10005;
      </CloseButton>
      <MainArea onClick={stopPropagation}>
        {hasPrev && (
          <NavButton
            $side="left"
            onClick={() => setIndex(index - 1)}
            aria-label="Previous"
          >
            &#8249;
          </NavButton>
        )}
        {currentMedia && currentMedia.videoUrl ? (
          <VideoFrame>
            <iframe
              src={`${currentMedia.videoUrl}?rel=0`}
              width="100%"
              height="100%"
              frameBorder={0}
              allow="autoplay; encrypted-media"
              title={name}
            />
          </VideoFrame>
        ) : (
          <ImageComponent
            url={currentMedia && getFullscreenImageUrl(currentMedia.url)}
            alt={name}
            type={IMAGE_UNAVAILABLE_SIZE.L}
          />
        )}
        {hasNext && (
          <NavButton
            $side="right"
            onClick={() => setIndex(index + 1)}
            aria-label="Next"
          >
            &#8250;
          </NavButton>
        )}
      </MainArea>
      {media.length > 1 && (
        <ThumbStrip onClick={stopPropagation}>
          {media.map((item, i) => (
            <ThumbItem
              key={i}
              $selected={i === index}
              onClick={() => setIndex(i)}
            >
              {item.videoUrl ? (
                <VideoThumb>&#9654;</VideoThumb>
              ) : (
                <ImageComponent
                  url={item.url}
                  alt=""
                  type={IMAGE_UNAVAILABLE_SIZE.S}
                />
              )}
            </ThumbItem>
          ))}
        </ThumbStrip>
      )}
    </Overlay>
  );
};

ProductMediaLightbox.propTypes = {
  media: PropTypes.array.isRequired,
  initialIndex: PropTypes.number,
  name: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default ProductMediaLightbox;
